import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {Box, IconButton, Paper, Popper, Stack, Tooltip, Typography} from "@mui/material";
import type {Editor} from "@tiptap/core";
import {useEditorState} from "@tiptap/react";
import {useEffect, useLayoutEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {selectedVariable} from "../editor/selectedVariable";
import {mountLayer} from "../editor/variablePickerPlugins";
import type {PickerVariable} from "../model/types";
import {tokenFor} from "../model/variableToken";
import {FLOATING_SHADOW} from "../shared/floatingShadow";
import {useFocusInAnotherEditor} from "./useFocusInAnotherEditor";
import {useVariableCatalogue} from "./useVariableCatalogue";

interface Props {
    editor: Editor;
    /** Defaults to the editor's own catalogue. */
    variables?: readonly PickerVariable[];
}

const COPIED_FEEDBACK_MS = 1200;

const MENU_MODIFIERS = [
    {name: "offset", options: {offset: [0, 8]}},
    {name: "preventOverflow", options: {padding: 8}},
];

/** `@mui/utils`' `visuallyHidden`, inlined: the dashboard does not depend on `@mui/utils` directly. */
const VISUALLY_HIDDEN = {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: "1px",
    margin: "-1px",
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    whiteSpace: "nowrap",
    width: "1px",
} as const;

/**
 * Anchored on the node's own DOM rather than through `@tiptap/react/menus`: that subpath ships no
 * `typesVersions`, so TypeScript 4.9 under `moduleResolution: node` cannot see its types.
 */
export function VariableBubbleMenu({editor, variables: variablesProp}: Props) {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [layer, setLayer] = useState<number | null>(null);
    const catalogue = useVariableCatalogue(editor);
    const variables = variablesProp ?? catalogue.variables;
    // The author moved on to another field; its blurred selection keeps no menu open.
    const focusElsewhere = useFocusInAnotherEditor(editor);

    const selected = useEditorState({
        editor,
        selector: ({editor: instance}) => selectedVariable(instance),
    });

    useEffect(() => {
        if (!copied) return;

        const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);

        return () => clearTimeout(timer);
    }, [copied]);

    useEffect(() => setCopied(false), [selected?.pos, selected?.key]);

    // The node's DOM is read after commit: during render the view may not have drawn the node yet.
    useLayoutEffect(() => {
        const dom = selected ? editor.view.nodeDOM(selected.pos) : null;
        setAnchor(dom instanceof HTMLElement ? dom : null);
        // Portalled to the body, so inside a Dialog it has to climb above the dialog the way the popup does.
        setLayer(dom instanceof HTMLElement ? Number(mountLayer(editor.view.dom)) : null);
    }, [editor, selected?.pos, selected?.key]); // eslint-disable-line react-hooks/exhaustive-deps

    const variable = selected ? variables.find(entry => entry.key === selected.key) : undefined;
    const Icon = variable?.icon;

    return (
        <Popper
            open={!!selected?.editable && !!anchor && !focusElsewhere}
            anchorEl={anchor}
            placement="top"
            modifiers={MENU_MODIFIERS}
            sx={{zIndex: theme => layer ?? theme.zIndex.modal - 1}}
            onMouseDown={event => event.preventDefault()}
        >
            <Paper variant="outlined" sx={{borderRadius: 2, boxShadow: FLOATING_SHADOW, px: 1, py: 0.5}}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                    {!!Icon && (
                        <Box sx={{display: "inline-flex", color: "text.secondary"}}>
                            <Icon fontSize="small"/>
                        </Box>
                    )}

                    <Typography variant="body2" sx={{pr: 0.5}}>
                        {variable?.label ?? selected?.key}
                    </Typography>

                    <Tooltip arrow title={t("Copy token")}>
                        <IconButton
                            size="small"
                            aria-label={t("Copy token")}
                            onMouseDown={event => event.preventDefault()}
                            // The clipboard is refused on an insecure origin and inside some frames,
                            // and an unhandled rejection there would surface as a runtime error.
                            onClick={() => selected && void navigator.clipboard
                                ?.writeText(tokenFor(selected.key, {format: selected.format, fallback: selected.fallback}))
                                .then(() => setCopied(true), () => undefined)}
                        >
                            {copied
                                ? <CheckIcon fontSize="small" color="success"/>
                                : <ContentCopyIcon fontSize="small"/>}
                        </IconButton>
                    </Tooltip>

                    <Tooltip arrow title={t("Delete")}>
                        <IconButton
                            size="small"
                            aria-label={t("Delete")}
                            onMouseDown={event => event.preventDefault()}
                            // Named by position: a host that moves the caret on pointer down would
                            // otherwise leave the menu acting on whatever the selection became.
                            onClick={() => selected && editor
                                .chain()
                                .focus()
                                .setNodeSelection(selected.pos)
                                .deleteSelection()
                                .run()}
                        >
                            <DeleteOutlineIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>

                    {/* The check icon alone says nothing to a screen reader. */}
                    <Box component="span" aria-live="polite" sx={VISUALLY_HIDDEN}>
                        {copied ? t("Copied") : ""}
                    </Box>
                </Stack>
            </Paper>
        </Popper>
    );
}

export default VariableBubbleMenu;
